import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { IrisQueryParams, IrisQueryParamsBuilder } from '@iris/api-query';
import { Observable, Subject, of } from 'rxjs';
import { map, debounceTime, switchMap, shareReplay, finalize, filter, startWith } from 'rxjs/operators';
import { ApiUrl, IrisApiClientCore } from '@iris/api-query';
import { IrisPage } from '@iris/common/models/page';
import { IrisResourceRoleI } from '@iris/common/models/IrisResourceRole';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import saveAs from 'file-saver';

@Injectable({ providedIn: 'root' })
@ApiUrl('/security/roles')
export class IrisResourceRolesService extends IrisApiClientCore {
  private readonly httpClient = inject(HttpClient);
  private readonly globalSandbox = inject(IrisGlobalSandbox);

  private readonly pendingAliases = new Set<string>();
  private triggerSub = new Subject<void>();
  private batchRequest$: Observable<IrisResourceRoleI[]>;
  private readonly DEBOUNCE_TIME_MS = 100;

  constructor() {
    super();
  }

  query(params: IrisQueryParams = new IrisQueryParams()): Observable<IrisPage<IrisResourceRoleI>> {
    return this.httpClient.get<IrisPage<IrisResourceRoleI>>(this.url(), { params: params.toObject() }).pipe(
    );
  }

  getRolebyAlias(alias: string): Observable<IrisResourceRoleI> {
    this.pendingAliases.add(alias);

    if (!this.batchRequest$) {
      this.triggerSub = new Subject<void>();
      this.batchRequest$ = this.triggerSub.pipe(
        startWith(null),
        debounceTime(this.DEBOUNCE_TIME_MS),
        switchMap(() => {
          const aliases = Array.from(this.pendingAliases);
          this.pendingAliases.clear();
          return this.executeBatchRequest(aliases);
        }),
        shareReplay(1),
        finalize(() => {
          this.batchRequest$ = null;
        }),
      );
    }

    this.triggerSub.next();

    return this.batchRequest$.pipe(
      filter(Boolean),
      map(response => response.find(entity => entity.alias === alias)),
    );
  }

  private executeBatchRequest(aliases: string[]): Observable<IrisResourceRoleI[]> {
    if (!aliases.length) {
      return of([] as IrisResourceRoleI[]);
    }

    const projectId = this.globalSandbox.currentProjectId();
    const params = new IrisQueryParamsBuilder()
      .urlParam('allowedPattern', `Project/${projectId}`)
      .onlyFields(['name', 'nameTranslated', 'alias'])
      .filter('alias', aliases)
      .toStructure();

    return this.query(params).pipe(map(res => res.elements));
  }

  @ApiUrl(`~/{id}`)
  getById(id: number, params: IrisQueryParams = new IrisQueryParams()): Observable<IrisResourceRoleI> {
    return this.httpClient.get<IrisResourceRoleI>(this.url({ id }), { params: params.toObject() });
  }

  @ApiUrl('~/active/resource-type/{resourceType}')
  getEnabled(resourceType: string): Observable<IrisResourceRoleI[]> {
    return this.httpClient.get<IrisResourceRoleI[]>(this.url({ resourceType }));
  }

  @ApiUrl('~/{id}/toggle-enabled')
  toggleEnabled(id: number): Observable<IrisResourceRoleI> {
    return this.httpClient.post<IrisResourceRoleI>(this.url({ id }), {});
  }

  @ApiUrl('~/{id}/{scope}')
  activateScope(id: number,scope: string): Observable<IrisResourceRoleI> {
    return this.httpClient.post<IrisResourceRoleI>(this.url({ id, scope }), {});
  }

  @ApiUrl('~/{id}/{scope}')
  deactivateScope(id: number, scope: string): Observable<IrisResourceRoleI> {
    return this.httpClient.delete<IrisResourceRoleI>(this.url({ id, scope }));
  }

  @ApiUrl('~/export')
  exportRoles(roleIds: number[]): Observable<unknown> {
    const params = { roleId: JSON.stringify(roleIds) };
    return this.httpClient.get(this.url(), { params, responseType: 'blob' }).pipe(
      map(response => {
        saveAs(response, 'exported_roles.json');
        return null;
      }),
    );
  }

  @ApiUrl('~/import')
  importRoles(file: File): Observable<unknown> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('updateExisting', 'true');

    return this.httpClient.post(this.url(), formData);
  }
}
